import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { usePortfolio } from "@/context/PortfolioContext";
import { usePrivacy } from "@/context/PrivacyContext";
import { SubPortfolioSelect } from "@/components/SubPortfolioSelect";
import { PerformanceChart, RangeSelector, AllocationDonut, AllocationLegend } from "@/components/Charts";
import { PortfolioStrip } from "@/components/dashboard/PortfolioStrip";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, RefreshCw } from "lucide-react";
import { fmtCurrency, fmtSigned, fmtPct, gainClass } from "@/lib/format";

export default function Performance() {
    const { selectedSub, setSelectedSub } = usePortfolio();
    const { mask } = usePrivacy();
    const [range, setRange] = useState("YTD");
    const [divOpen, setDivOpen] = useState(false);
    const qc = useQueryClient();
    const refreshData = () => qc.invalidateQueries();

    const perf = useQuery({ queryKey: ["perf-page", selectedSub, range], queryFn: async () => (await api.get(`/portfolio/performance?range=${range}&sub=${selectedSub}`)).data });
    const ytd = useQuery({ queryKey: ["perf-ytd", selectedSub], queryFn: async () => (await api.get(`/portfolio/performance?range=YTD&sub=${selectedSub}`)).data });
    const qtd = useQuery({ queryKey: ["perf-qtd", selectedSub], queryFn: async () => (await api.get(`/portfolio/performance?range=QTD&sub=${selectedSub}`)).data });
    const summary = useQuery({ queryKey: ["perf-summary", selectedSub], queryFn: async () => (await api.get(`/portfolio/summary?sub=${selectedSub}`)).data });
    const divs = useQuery({ queryKey: ["perf-divs", selectedSub], queryFn: async () => (await api.get(`/portfolio/dividends?sub=${selectedSub}`)).data });
    const alloc = useQuery({ queryKey: ["perf-alloc", selectedSub], queryFn: async () => (await api.get(`/portfolio/allocation?sub=${selectedSub}`)).data });
    const bench = useQuery({ queryKey: ["perf-bench", selectedSub], queryFn: async () => (await api.get(`/portfolio/benchmark?sub=${selectedSub}`)).data });

    const s = summary.data;
    const m = perf.data?.metrics;
    const ytdM = ytd.data?.metrics;
    const qtdM = qtd.data?.metrics;
    const series = perf.data?.series || [];
    const unrealPct = s && s.invested > 0 ? (s.unrealized_pl / s.invested) * 100 : null;

    const exportDivCsv = () => {
        const rows = divs.data?.by_security_ytd || [];
        const year = new Date().getFullYear();
        const lines = [["Ticker", "Dividends YTD ($)"], ...rows.map((r) => [r.ticker, r.amount.toFixed(2)]),
            ["Total", (divs.data?.this_year ?? 0).toFixed(2)]];
        const csv = lines.map((l) => l.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `dividends_${year}${selectedSub !== "ALL" ? "_" + selectedSub : ""}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const AllocPanel = ({ data, testId }) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
            <AllocationDonut data={data || []} testId={testId} />
            <AllocationLegend data={data || []} />
        </div>
    );

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-2xl font-bold tracking-tight">Performance</h1>
                    <p className="text-sm text-muted-foreground">Returns, benchmarks & allocation analytics</p>
                </div>
                <div className="flex items-center gap-2">
                    <SubPortfolioSelect value={selectedSub} onChange={setSelectedSub} testId="perf-sub-select" />
                    <Button variant="outline" size="icon" onClick={refreshData} disabled={perf.isFetching || summary.isFetching} title="Refresh market data" data-testid="perf-refresh-btn">
                        <RefreshCw className={`w-4 h-4 ${(perf.isFetching || summary.isFetching) ? "animate-spin" : ""}`} />
                    </Button>
                </div>
            </div>

            {/* Metrics */}
            {summary.isLoading || !s ? <Skeleton className="h-[64px] rounded-xl" /> : (
                <PortfolioStrip cells={[
                    { testId: "perf-return", label: "Total Return", value: mask(fmtSigned(s.total_return)), valueClass: gainClass(s.total_return), delta: s.total_return, deltaPct: fmtPct(s.total_return_pct) },
                    { testId: "perf-unrealized", label: "Unrealized P/L", value: mask(fmtSigned(s.unrealized_pl)), valueClass: gainClass(s.unrealized_pl), delta: s.unrealized_pl, deltaPct: unrealPct != null ? fmtPct(unrealPct) : "" },
                    { testId: "perf-dividends", label: "Dividends (YTD)", value: mask(fmtCurrency(divs.data?.this_year ?? 0)), sub: "view detail ›", onClick: () => setDivOpen(true) },
                    { testId: "perf-ytd", label: "YTD Return", value: ytdM ? mask(fmtSigned(ytdM.abs_change)) : "—", valueClass: gainClass(ytdM?.abs_change), delta: ytdM?.abs_change, deltaPct: ytdM ? fmtPct(ytdM.pct_change) : "" },
                    { testId: "perf-qtd", label: "QTD Return", value: qtdM ? mask(fmtSigned(qtdM.abs_change)) : "—", valueClass: gainClass(qtdM?.abs_change), delta: qtdM?.abs_change, deltaPct: qtdM ? fmtPct(qtdM.pct_change) : "" },
                ]} />
            )}

            {/* Chart vs benchmarks */}
            <Card className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <div>
                        <h2 className="font-heading text-base font-semibold">Portfolio vs S&P 500 & NASDAQ</h2>
                        <p className="text-xs text-muted-foreground">Benchmarks scaled to your starting portfolio value for comparison</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <RangeSelector value={range} onChange={setRange} ranges={["1M", "3M", "6M", "YTD", "1Y", "ALL"]} testPrefix="perf-range" />
                    </div>
                </div>
                {perf.isLoading ? <Skeleton className="h-[260px] rounded-lg" /> : <PerformanceChart series={series} benchmarks={perf.data?.benchmarks} height={260} />}
            </Card>

            {/* Benchmark vs S&P 500 */}
            <Card className="p-4">
                <div className="mb-2">
                    <h2 className="font-heading text-base font-semibold">Return vs S&amp;P 500</h2>
                    <p className="text-xs text-muted-foreground">Each holding compared to the same dollars invested in the S&amp;P 500 over its own holding period (buy → sell, or to date)</p>
                </div>
                {bench.isLoading ? <Skeleton className="h-56 rounded-lg" /> : (bench.data?.securities?.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground py-4" data-testid="benchmark-empty">No buy/sell activity to compare yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="benchmark-table">
                            <thead className="border-b border-border">
                                <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                    <th className="px-3 py-2 text-left">Security</th>
                                    <th className="px-3 py-2 text-right">Invested</th>
                                    <th className="px-3 py-2 text-right">Security Gain $</th>
                                    <th className="px-3 py-2 text-right">Security %</th>
                                    <th className="px-3 py-2 text-right">S&amp;P 500 Gain $</th>
                                    <th className="px-3 py-2 text-right">S&amp;P 500 %</th>
                                    <th className="px-3 py-2 text-right">Δ vs S&amp;P</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bench.data.securities.map((r) => (
                                    <tr key={r.ticker} className="border-b border-border/50 hover:bg-muted/40 transition-colors" data-testid={`benchmark-row-${r.ticker}`}>
                                        <td className="px-3 py-1.5 text-left">
                                            <div className="font-semibold num">{r.ticker}</div>
                                            <div className="text-[10px] text-muted-foreground num">{r.first_date} → {r.held ? "to date" : r.last_date}</div>
                                        </td>
                                        <td className="px-3 py-1.5 text-right num">{mask(fmtCurrency(r.invested, { decimals: 0 }))}</td>
                                        <td className={`px-3 py-1.5 text-right num ${gainClass(r.security_gain)}`}>{mask(fmtSigned(r.security_gain, { decimals: 0 }))}</td>
                                        <td className={`px-3 py-1.5 text-right num ${gainClass(r.security_pct)}`}>{fmtPct(r.security_pct)}</td>
                                        <td className={`px-3 py-1.5 text-right num ${gainClass(r.sp_gain)}`}>{mask(fmtSigned(r.sp_gain, { decimals: 0 }))}</td>
                                        <td className={`px-3 py-1.5 text-right num ${gainClass(r.sp_pct)}`}>{fmtPct(r.sp_pct)}</td>
                                        <td className={`px-3 py-1.5 text-right num font-medium ${gainClass(r.security_pct - r.sp_pct)}`}>{fmtPct(r.security_pct - r.sp_pct)}</td>
                                    </tr>
                                ))}
                                {bench.data.aggregate?.invested > 0 && (
                                    <tr className="font-semibold bg-muted/30" data-testid="benchmark-total-row">
                                        <td className="px-3 py-1.5 text-left">Portfolio{selectedSub !== "ALL" ? ` · ${selectedSub}` : ""}</td>
                                        <td className="px-3 py-1.5 text-right num">{mask(fmtCurrency(bench.data.aggregate.invested, { decimals: 0 }))}</td>
                                        <td className={`px-3 py-1.5 text-right num ${gainClass(bench.data.aggregate.security_gain)}`}>{mask(fmtSigned(bench.data.aggregate.security_gain, { decimals: 0 }))}</td>
                                        <td className={`px-3 py-1.5 text-right num ${gainClass(bench.data.aggregate.security_pct)}`}>{fmtPct(bench.data.aggregate.security_pct)}</td>
                                        <td className={`px-3 py-1.5 text-right num ${gainClass(bench.data.aggregate.sp_gain)}`}>{mask(fmtSigned(bench.data.aggregate.sp_gain, { decimals: 0 }))}</td>
                                        <td className={`px-3 py-1.5 text-right num ${gainClass(bench.data.aggregate.sp_pct)}`}>{fmtPct(bench.data.aggregate.sp_pct)}</td>
                                        <td className={`px-3 py-1.5 text-right num ${gainClass(bench.data.aggregate.security_pct - bench.data.aggregate.sp_pct)}`}>{fmtPct(bench.data.aggregate.security_pct - bench.data.aggregate.sp_pct)}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* Allocation analytics */}
            <Card className="p-4">
                <h2 className="font-heading text-base font-semibold mb-4">Allocation</h2>
                {alloc.isLoading ? <Skeleton className="h-64 rounded-lg" /> : (
                    <Tabs defaultValue="security">
                        <TabsList data-testid="allocation-tabs">
                            <TabsTrigger value="security" data-testid="alloc-tab-security">By Security</TabsTrigger>
                            <TabsTrigger value="sub" data-testid="alloc-tab-sub">By Sub-portfolio</TabsTrigger>
                            <TabsTrigger value="sector" data-testid="alloc-tab-sector">By Sector</TabsTrigger>
                        </TabsList>
                        <TabsContent value="security" className="mt-5"><AllocPanel data={alloc.data?.by_security} testId="alloc-security-chart" /></TabsContent>
                        <TabsContent value="sub" className="mt-5"><AllocPanel data={alloc.data?.by_sub_portfolio} testId="alloc-sub-chart" /></TabsContent>
                        <TabsContent value="sector" className="mt-5"><AllocPanel data={alloc.data?.by_sector} testId="alloc-sector-chart" /></TabsContent>
                    </Tabs>
                )}
            </Card>

            <Dialog open={divOpen} onOpenChange={setDivOpen}>
                <DialogContent className="max-w-md" data-testid="dividend-drilldown-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Dividends paid this year{selectedSub !== "ALL" ? ` · ${selectedSub}` : ""}</DialogTitle>
                    </DialogHeader>
                    {(divs.data?.by_security_ytd || []).length > 0 && (
                        <div className="flex justify-end -mt-2">
                            <Button variant="outline" size="sm" onClick={exportDivCsv} data-testid="dividend-export-btn"><Download className="w-3.5 h-3.5 mr-1.5" />Export CSV</Button>
                        </div>
                    )}
                    <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                        {(divs.data?.by_security_ytd || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground py-4" data-testid="dividend-drilldown-empty">No dividends recorded this year.</p>
                        ) : (divs.data.by_security_ytd.map((d) => (
                            <div key={d.ticker} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/50" data-testid={`dividend-row-${d.ticker}`}>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center text-[10px] font-bold num">{d.ticker.slice(0, 4)}</div>
                                    <span className="font-medium text-sm num">{d.ticker}</span>
                                </div>
                                <span className="num font-semibold text-sm">{mask(fmtCurrency(d.amount))}</span>
                            </div>
                        )))}
                        <div className="flex items-center justify-between py-2 px-2 border-t border-border mt-1 font-semibold text-sm">
                            <span>Total YTD</span>
                            <span className="num">{mask(fmtCurrency(divs.data?.this_year ?? 0))}</span>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
